import { ButtonProps, useDisclosure } from '@chakra-ui/react';
import { ApprovalStatusEnum, QuoteStatusEnum } from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { handleApiErrorToast } from '../../../../../api/axios';
import { MButton } from '../../../../../components/Monetize';
import { useExperienceConfiguration } from '../../../../../hooks';
import { useIsTenantEsignConfigured } from '../../../../../hooks/useIsTenantEsignConfigured';
import { useAuth } from '../../../../../services/auth0';
import { useQuoteContext } from '../../../Quote/quoteContext';
import { updateQuoteQueryCache } from '../../../Quote/quoteUtils';
import { QuoteEditConfirmationModal } from './QuoteEditConfirmationModal';

interface QuoteEditButtonProps {
  buttonProps?: ButtonProps;
}

/**
 * Quote Edit Button
 * Optionally show a confirmation modal if required based on quote state
 * If quote has been sent for e-signature, then warn the user that it will be retracted
 * If quote has approved approvals, then warn the user
 * If quote has approved approvals with optional admin override, then the modal will allow admin to retain some approvals
 */
export const QuoteEditButton = ({ buttonProps }: QuoteEditButtonProps) => {
  const { quoteData, setIsInternalView } = useQuoteContext();
  const { quote, transitionQuoteStatus } = quoteData;
  const queryClient = useQueryClient();
  const isTenantEsignConfigured = useIsTenantEsignConfigured();
  const [isLoading, setIsLoading] = useState(false);
  const isEsignEnabled = isTenantEsignConfigured && !!quote?.requiresEsign;
  const confirmationModalState = useDisclosure();
  const { currentTenantUserHasRole } = useAuth();
  const { allowRetainApprovalsRoles } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );

  const canRetainApprovals = currentTenantUserHasRole(
    allowRetainApprovalsRoles,
  );

  const handleTransitionStatus = async (retainApprovalIds?: string[]) => {
    try {
      if (!quote) {
        return;
      }
      setIsLoading(true);
      const extraData = retainApprovalIds ? { retainApprovalIds } : undefined;
      const quoteResponse = await transitionQuoteStatus(quote.id, {
        newState: QuoteStatusEnum.DRAFT,
        extraData,
      });

      if (quoteResponse) {
        updateQuoteQueryCache(queryClient, quoteResponse);
      }
      setIsInternalView(true);
    } catch (ex) {
      handleApiErrorToast(ex);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = async () => {
    try {
      if (!quote) {
        return null;
      }
      setIsLoading(true);

      const hasDeclinedApprovals = quote.approvals.some(
        (approval) => approval.status === ApprovalStatusEnum.DECLINED,
      );

      const hasApprovedApprovals = quote.approvals.some(
        (approval) => approval.status === ApprovalStatusEnum.APPROVED,
      );

      const hasSentEsign =
        isEsignEnabled && quote.status === QuoteStatusEnum.SENT;

      let showApprovalModal = false;
      if (hasSentEsign) {
        showApprovalModal = true;
      }
      if (hasApprovedApprovals && !hasDeclinedApprovals) {
        showApprovalModal = true;
      }
      if (canRetainApprovals && hasApprovedApprovals) {
        showApprovalModal = true;
      }

      if (showApprovalModal) {
        confirmationModalState.onOpen();
        return;
      }

      await handleTransitionStatus();
    } catch (ex) {
      handleApiErrorToast(ex);
    } finally {
      setIsLoading(false);
    }
  };

  if (!quote) {
    return null;
  }

  return (
    <Fragment>
      {confirmationModalState.open && (
        <QuoteEditConfirmationModal
          isEsignEnabled={isEsignEnabled}
          onClose={confirmationModalState.onClose}
          onTransitionStatus={handleTransitionStatus}
          canRetainApprovals={canRetainApprovals}
          quote={quote}
        />
      )}
      <MButton
        mr="2"
        type="button"
        variant="tertiary"
        size="sm"
        isDisabled={isLoading}
        isLoading={isLoading}
        {...buttonProps}
        onClick={handleClick}
      >
        Edit Quote
      </MButton>
    </Fragment>
  );
};
